import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Zap, Loader2 } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { http, formatApiErrorDetail } from "@/lib/api";
import { openCheckout } from "@/lib/razorpay";
import { toast } from "sonner";

const PLANS = [
  {
    tier: "starter",
    name: "Starter",
    price: "$29",
    period: "/mo",
    leads: "300 enrichments",
    model: "LeadIQ Atom",
    perks: ["Standard render", "Tech stack + news extraction", "CSV export", "1 user"],
    highlight: false,
  },
  {
    tier: "growth",
    name: "Growth",
    price: "$149",
    period: "/mo",
    leads: "1,500 enrichments",
    model: "LeadIQ Spark",
    perks: [
      "Everything in Starter",
      "AI fit scoring (we scan your website + ICP wizard)",
      "5× sharper analysis (LeadIQ Spark)",
      "LeadIQ email verification + finder",
      "Bulk CSV (10× faster uploads)",
      "Smart fallback when emails aren't in our directory",
      "3 users (+$5/seat/mo for extras)",
    ],
    highlight: true,
  },
  {
    tier: "scale",
    name: "Scale",
    price: "$599",
    period: "/mo",
    leads: "6,000 enrichments",
    model: "LeadIQ Apex",
    perks: [
      "Everything in Growth",
      "Frontier model (LeadIQ Apex)",
      "Premium reasoning + outreach lines",
      "Salesforce / HubSpot push",
      "5 users (+$5/seat/mo for extras)",
    ],
    highlight: false,
  },
];

export default function UpgradeDialog({ open, onClose }) {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(null);

  const onUpgrade = async (tier) => {
    setBusy(tier);
    try {
      const { data } = await http.post("/billing/tier/checkout", {
        tier,
        billing_cycle: "monthly",
      });

      if (data.demo) {
        toast.success(data.message || `Switched to ${tier}`);
        await refresh?.();
        onClose();
        setBusy(null);
        return;
      }
      if (data.affiliate && data.affiliate.applied) {
        toast.success(
          `${data.affiliate.partner_name} discount applied · ${data.affiliate.discount_pct}% off ($${data.affiliate.discount_amount_usd.toFixed(2)} saved)`,
          { duration: 6000 }
        );
      }
      if (data.tax && (data.tax.tax_amount_usd || 0) > 0) {
        toast.info(
          `Charging $${data.tax.total_usd.toFixed(2)} · $${data.tax.subtotal_usd.toFixed(2)} + ${data.tax.tax_label} $${data.tax.tax_amount_usd.toFixed(2)}`,
          { duration: 5000 }
        );
      }

      await openCheckout({
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "LeadIQ Pro",
        description: `${tier.charAt(0).toUpperCase() + tier.slice(1)} plan · monthly`,
        prefill: {
          name: data.user?.name || "",
          email: data.user?.email || "",
        },
        theme: { color: "#0a0a0a" },
        modal: {
          ondismiss: () => setBusy(null),
        },
        handler: async (resp) => {
          try {
            await http.post("/billing/tier/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Plan upgraded to ${tier.charAt(0).toUpperCase() + tier.slice(1)}`);
            await refresh?.();
            onClose();
          } catch (err) {
            toast.error(
              formatApiErrorDetail(err?.response?.data?.detail) || "Verification failed"
            );
          } finally {
            setBusy(null);
          }
        },
        onFailed: (resp) => {
          toast.error("Payment failed: " + (resp?.error?.description || "unknown error"));
          setBusy(null);
        },
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-3xl tracking-tight">
            Unlock deeper intelligence.
          </DialogTitle>
          <p className="text-sm text-ink-500">
            Each tier uses a more powerful AI model — outputs measurably get smarter, not just longer.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          {PLANS.map((p) => {
            const isCurrent = (user?.tier || "free") === p.tier;
            return (
              <div
                key={p.tier}
                data-testid={`plan-${p.tier}`}
                className={`relative p-5 border rounded-sm flex flex-col ${
                  p.highlight
                    ? "bg-ink-900 text-white border-ink-900"
                    : "bg-white border-ink-100"
                } ${isCurrent ? "ring-2 ring-brand ring-offset-2" : ""}`}
              >
                {p.highlight && (
                  <div className="absolute -top-2.5 left-5 inline-flex items-center gap-1 bg-brand text-white text-[9px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm">
                    <Zap className="w-2.5 h-2.5" /> Most popular
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-2.5 right-5 bg-brand text-white text-[9px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm">
                    Current
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-widestx font-mono opacity-70">
                  {p.name}
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display font-black text-4xl tracking-tighter">{p.price}</span>
                  <span className={`text-xs ${p.highlight ? "text-white/60" : "text-ink-400"}`}>
                    {p.period}
                  </span>
                </div>
                <div
                  className={`text-xs font-mono mt-1 ${
                    p.highlight ? "text-white/80" : "text-ink-500"
                  }`}
                >
                  {p.leads}
                </div>

                <div
                  className={`mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widestx font-mono px-2 py-1 rounded-sm w-fit ${
                    p.highlight ? "bg-brand/30 text-white" : "bg-ink-100 text-ink-700"
                  }`}
                >
                  <span className="opacity-70">model:</span>
                  <span className="font-semibold normal-case tracking-normal">{p.model}</span>
                </div>

                <ul className="space-y-2 my-5 flex-1">
                  {p.perks.map((perk, i) => (
                    <li key={i} className="text-xs flex items-start gap-2">
                      <Check
                        className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${
                          p.highlight ? "text-brand" : "text-[#2E7D32]"
                        }`}
                      />
                      <span className={p.highlight ? "text-white/90" : "text-ink-700"}>{perk}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`plan-${p.tier}-cta`}
                  onClick={() => onUpgrade(p.tier)}
                  disabled={busy !== null || isCurrent}
                  className={`w-full text-xs font-medium uppercase tracking-widestx py-3 rounded-sm transition-colors disabled:opacity-50 ${
                    p.highlight
                      ? "bg-brand hover:bg-brand-hover text-white"
                      : "bg-ink-100 hover:bg-ink-200 text-ink-900"
                  }`}
                >
                  {busy === p.tier ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
                  ) : isCurrent ? (
                    "Current plan"
                  ) : (
                    `Switch to ${p.name}`
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 text-center mt-4">
          Demo mode · no payment required · Stripe Billing wired in production
        </p>
      </DialogContent>
    </Dialog>
  );
}
